// src/components/institution/modals/ResourceModal.jsx
import React, { useState } from 'react';
import { 
  X, Download, Eye, FileText, Package, BookOpen, Briefcase, 
  Monitor, Chair, Shield, Video, Calendar, AlertTriangle, 
  CheckCircle, Clock, MapPin, DollarSign, ExternalLink,
  Hash, Tag, Users, Lock, Globe, Image as ImageIcon,
  Camera, Paperclip, File, ChevronLeft, ChevronRight
} from 'lucide-react';

// Resource type configurations
const RESOURCE_TYPES = {
  physical: {
    label: 'Physical Resources',
    icon: Package,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-900',
    borderColor: 'border-blue-200'
  },
  learning: {
    label: 'Learning & Teaching Materials',
    icon: BookOpen,
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-900',
    borderColor: 'border-green-200'
  },
  management: {
    label: 'Management Resources',
    icon: Briefcase,
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-900',
    borderColor: 'border-purple-200'
  }
};

const STATUS_CONFIG = {
  available: { label: 'Available', color: 'text-green-600 bg-green-100', icon: CheckCircle },
  inUse: { label: 'In Use', color: 'text-blue-600 bg-blue-100', icon: Clock },
  maintenance: { label: 'Maintenance', color: 'text-yellow-600 bg-yellow-100', icon: AlertTriangle },
  damaged: { label: 'Damaged', color: 'text-red-600 bg-red-100', icon: AlertTriangle },
  retired: { label: 'Retired', color: 'text-gray-600 bg-gray-100', icon: Package }
};

const ACCESS_LEVEL_CONFIG = {
  public: { label: 'Public', icon: Globe, color: 'text-green-600' },
  students: { label: 'Students Only', icon: Users, color: 'text-blue-600' },
  staff: { label: 'Staff Only', icon: Users, color: 'text-indigo-600' },
  faculty: { label: 'Faculty Only', icon: Users, color: 'text-purple-600' },
  admin: { label: 'Admin Only', icon: Lock, color: 'text-red-600' }
};

export const ResourceModal = ({ resource, onClose, styling, isAdmin }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!resource) return null;

  const typeConfig = RESOURCE_TYPES[resource.resourceType] || RESOURCE_TYPES.learning;
  const statusConfig = resource.status ? STATUS_CONFIG[resource.status] : null;
  const accessConfig = ACCESS_LEVEL_CONFIG[resource.accessLevel] || ACCESS_LEVEL_CONFIG.public;
  const TypeIcon = typeConfig.icon;
  
  const hasImages = resource.images && resource.images.length > 0;
  const hasDocuments = resource.documents && resource.documents.length > 0;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date.toDate ? date.toDate() : date).toLocaleDateString();
  };

  const getFileTypeIcon = (fileType) => {
    if (fileType?.includes('image')) return ImageIcon;
    if (fileType?.includes('pdf')) return FileText;
    if (fileType?.includes('video')) return Video;
    return File;
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % resource.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + resource.images.length) % resource.images.length);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`p-6 border-b border-gray-200 ${typeConfig.bgColor}`}>
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4 flex-1">
              <div className={`p-3 rounded-lg ${typeConfig.bgColor} ${typeConfig.borderColor} border-2`}>
                <TypeIcon className={`h-6 w-6 ${typeConfig.textColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-gray-900 truncate">
                    {resource.title}
                  </h2>
                  {/* Status Badge for Physical Resources */}
                  {resource.resourceType === 'physical' && statusConfig && (
                    <span className={`px-3 py-1 text-sm font-medium rounded-full flex items-center gap-1 ${statusConfig.color}`}>
                      <statusConfig.icon className="h-4 w-4" />
                      {statusConfig.label}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <TypeIcon className="h-4 w-4" />
                    {typeConfig.label}
                  </span>
                  <span className="capitalize">{resource.category?.replace(/([A-Z])/g, ' $1')}</span>
                  {resource.assetId && (
                    <span className="flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {resource.assetId}
                    </span>
                  )}
                </div>

                {/* Access Level */}
                <div className="flex items-center gap-2 mt-2">
                  <accessConfig.icon className={`h-4 w-4 ${accessConfig.color}`} />
                  <span className={`text-sm font-medium ${accessConfig.color}`}>
                    {accessConfig.label}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Images and Description */}
            <div className="space-y-6">
              {/* Image Gallery */}
              {hasImages && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Camera className="h-5 w-5 text-blue-600" />
                    Resource Images ({resource.images.length})
                  </h3>
                  
                  {/* Main Image Display */}
                  <div className="relative">
                    <img
                      src={resource.images[currentImageIndex].url}
                      alt={resource.images[currentImageIndex].name}
                      className="w-full h-64 object-cover rounded-lg border border-gray-200"
                    />
                    
                    {/* Navigation Arrows */}
                    {resource.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                        
                        {/* Image Counter */}
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {resource.images.length}
                        </div>
                      </>
                    )}
                    
                    {/* View Full Size Button */}
                    <button
                      onClick={() => window.open(resource.images[currentImageIndex].url, '_blank')}
                      className="absolute top-2 right-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-lg"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Thumbnail Navigation */}
                  {resource.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {resource.images.map((image, index) => (
                        <button
                          key={image.id}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                            index === currentImageIndex ? 'border-blue-500' : 'border-gray-200'
                          }`}
                        >
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-600">
                    Current: {resource.images[currentImageIndex].name}
                  </p>
                </div>
              )}

              {/* Description */}
              {resource.description && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{resource.description}</p>
                </div>
              )}

              {/* Supporting Documents */}
              {hasDocuments && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Paperclip className="h-5 w-5 text-green-600" />
                    Supporting Documents ({resource.documents.length})
                  </h3>
                  
                  <div className="space-y-3">
                    {resource.documents.map((document) => {
                      const FileIcon = getFileTypeIcon(document.type);
                      return (
                        <div key={document.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {document.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(document.size)} • Uploaded {formatDate(document.uploadedAt)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => window.open(document.url, '_blank')}
                              className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-200"
                              title="View Document"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = document.url;
                                link.download = document.name;
                                link.click();
                              }}
                              className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-200"
                              title="Download Document"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Resource Details */}
            <div className="space-y-6">
              {/* Physical Resource Details */}
              {resource.resourceType === 'physical' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    Asset Information
                  </h3>
                  
                  <div className="space-y-3">
                    {resource.location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Location: {resource.location}</span>
                      </div>
                    )}
                    
                    {resource.value && (
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Value: ${resource.value}</span>
                      </div>
                    )}
                    
                    {resource.condition && (
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Condition: {resource.condition}</span>
                      </div>
                    )}
                    
                    {resource.purchaseDate && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Purchased: {formatDate(resource.purchaseDate)}</span>
                      </div>
                    )}
                    
                    {resource.warranty && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <Shield className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-700">Warranty Information:</span>
                        </div>
                        <p className="text-sm text-gray-600 ml-7">{resource.warranty}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Learning Resource Details */}
              {resource.resourceType === 'learning' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-green-600" />
                    Educational Details
                  </h3>
                  
                  <div className="space-y-3">
                    {resource.subject && (
                      <div className="flex items-center gap-3">
                        <BookOpen className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Subject: {resource.subject}</span>
                      </div>
                    )}
                    
                    {resource.gradeLevel && (
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Grade Level: {resource.gradeLevel}</span>
                      </div>
                    )}
                    
                    {resource.difficulty && (
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Difficulty: {resource.difficulty}</span>
                      </div>
                    )}
                    
                    {resource.duration && (
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Duration: {resource.duration}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Management Resource Details */}
              {resource.resourceType === 'management' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                    Management Details
                  </h3>
                  
                  <div className="space-y-3">
                    {resource.department && (
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Department: {resource.department}</span>
                      </div>
                    )}
                    
                    {resource.priority && (
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Priority: {resource.priority}</span>
                      </div>
                    )}
                    
                    {resource.reviewDate && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Review Date: {formatDate(resource.reviewDate)}</span>
                      </div>
                    )}
                    
                    {resource.approvedBy && (
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Approved By: {resource.approvedBy}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* File/Digital Details */}
              {(resource.resourceType === 'learning' || resource.resourceType === 'management') && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-indigo-600" />
                    File Information
                  </h3>
                  
                  <div className="space-y-3">
                    {resource.fileSize && (
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">File Size: {formatFileSize(resource.fileSize)}</span>
                      </div>
                    )}
                    
                    {resource.version && (
                      <div className="flex items-center gap-3">
                        <Tag className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Version: {resource.version}</span>
                      </div>
                    )}
                    
                    {resource.uploadedBy && (
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">Uploaded By: {resource.uploadedBy}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {resource.tags && resource.tags.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-medium text-gray-900">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {resource.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-sm text-gray-500 border-t border-gray-200 pt-4">
                <div className="space-y-1">
                  <div>Created: {formatDate(resource.createdAt)}</div>
                  <div>Updated: {formatDate(resource.updatedAt)}</div>
                  {resource.downloads && <div>Downloads: {resource.downloads}</div>}
                  {resource.views && <div>Views: {resource.views}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {hasImages && `${resource.images.length} images`}
            {hasImages && hasDocuments && ' • '}
            {hasDocuments && `${resource.documents.length} documents`}
            {resource.downloads && ` • ${resource.downloads} downloads`}
            {resource.views && ` • ${resource.views} views`}
          </div>
          
          <div className="flex gap-3">
            {resource.fileUrl && (
              <a
                href={resource.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Eye className="h-4 w-4" />
                View Resource
              </a>
            )}
            
            {resource.downloadUrl && (
              <a
                href={resource.downloadUrl}
                download={resource.title}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download
              </a>
            )}

            {resource.externalUrl && (
              <a
                href={resource.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Open Link
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};